import { useMemo, useState } from 'react';
import { services } from '../../../data/services';
import { approvedReviews } from '../../../data/reviews';
import { buildReviewMessage, buildWhatsAppUrl } from '../../../utils/whatsapp';

const initialReview = {
  customerName: '',
  serviceId: '',
  rating: 0,
  comment: '',
  consent: false,
};

export default function Reviews() {
  const [review, setReview] = useState(initialReview);
  const selectedService = services.find((service) => service.id === review.serviceId);

  const message = useMemo(() => buildReviewMessage({
    customerName: review.customerName,
    serviceName: selectedService?.name || '',
    rating: review.rating,
    comment: review.comment,
  }), [review, selectedService]);

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setReview((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitReview = (event) => {
    event.preventDefault();
    window.open(buildWhatsAppUrl(message), '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="section reviews" id="resenas">
      <div className="container reviews__layout">
        <div className="reviews__intro">
          <p className="eyebrow">Reseñas</p>
          <h2 className="section-title">Tu experiencia también importa.</h2>
          <p>Cuéntanos cómo fue tu atención. Tu reseña llegará directamente a Poleth por WhatsApp para ser revisada antes de cualquier publicación.</p>

          <ol className="reviews__process" aria-label="Proceso de revisión">
            <li><span>01</span><p>Completa todos los campos y selecciona de una a cinco estrellas.</p></li>
            <li><span>02</span><p>La reseña se enviará de forma privada mediante WhatsApp.</p></li>
            <li><span>03</span><p>Ningún comentario se publica automáticamente en la página.</p></li>
          </ol>

        </div>

        <div className="reviews__content">
          <form className="review-form" onSubmit={submitReview}>
            <div className="review-form__heading">
              <span>Comparte tu opinión</span>
              <small>Todos los campos son obligatorios</small>
            </div>

            <div className="field">
              <label htmlFor="reviewCustomerName">Tu nombre <span aria-hidden="true">*</span></label>
              <input
                id="reviewCustomerName"
                name="customerName"
                value={review.customerName}
                onChange={updateField}
                autoComplete="name"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="reviewServiceId">Servicio realizado <span aria-hidden="true">*</span></label>
              <select id="reviewServiceId" name="serviceId" value={review.serviceId} onChange={updateField} required>
                <option value="">Selecciona un servicio</option>
                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
              </select>
            </div>

            <fieldset className="review-rating">
              <legend>Tu calificación <span aria-hidden="true">*</span></legend>
              <div className="review-rating__stars">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label className={review.rating >= value ? 'review-rating__star review-rating__star--selected' : 'review-rating__star'} key={value}>
                    <input
                      type="radio"
                      name="rating"
                      value={value}
                      checked={Number(review.rating) === value}
                      onChange={updateField}
                      required
                    />
                    <span aria-hidden="true">★</span>
                    <span className="sr-only">{value} {value === 1 ? 'estrella' : 'estrellas'}</span>
                  </label>
                ))}
              </div>
              <p>{review.rating ? `${review.rating} de 5 estrellas` : 'Selecciona una puntuación'}</p>
            </fieldset>

            <div className="field">
              <label htmlFor="reviewComment">Tu comentario <span aria-hidden="true">*</span></label>
              <textarea
                id="reviewComment"
                name="comment"
                rows="4"
                minLength="10"
                maxLength="700"
                value={review.comment}
                onChange={updateField}
                placeholder="Cuéntanos qué te gustó o qué podríamos mejorar."
                required
              />
              <span className="field__counter">{review.comment.length}/700</span>
            </div>

            <label className="check-line">
              <input type="checkbox" name="consent" checked={review.consent} onChange={updateField} required />
              <span>Autorizo que mi reseña sea revisada y, si corresponde, publicada posteriormente por Mujer Virtuosa.</span>
            </label>

            <p className="review-form__notice">La reseña se envía a revisión privada. No se publicará automáticamente en este sitio.</p>

            <button className="button button--gold" type="submit">Enviar reseña para revisión</button>
          </form>
        </div>
      </div>

      {approvedReviews.length > 0 ? (
        <div className="container reviews__published">
          <div className="reviews__published-heading">
            <p className="eyebrow">Experiencias compartidas</p>
            <small>Reseña verificada por Mujer Virtuosa</small>
          </div>
          <div className="reviews__approved" aria-label="Reseñas publicadas">
            {approvedReviews.map((item) => (
              <article key={item.id}>
                <p aria-label={`${item.rating} de 5 estrellas`}>{'★'.repeat(item.rating)}</p>
                <blockquote>“{item.comment}”</blockquote>
                <footer>
                  <cite>{item.customerName}</cite>
                  <small>{item.serviceName}{item.isDemo ? ' · Demostración' : ''}</small>
                </footer>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
